<template>
  <b-container>
    <h1 v-translate>Company Area</h1>
    <hr>
    <div class="row">
      <div class="col-md-3">
        <company-navigation />
      </div>
      <div class="col-md-9 personal-info">
        <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown=0" @dismiss-count-down="countDownChanged">
          <p class="mb-0" v-translate>Webhook Data was updated!</p>
        </b-alert>
        <h3 v-translate>API/Webhook</h3>
        <b-alert show variant="warning">
          <h3 v-translate>Important!</h3>
          <p v-translate>All the configurations contained in this page are <strong>very sensitive</strong> and could <strong>make your integration with us stop working in a snap</strong>.</p>
          <p><strong v-translate>Please, be very careful and don't make any change if you don't really know what are you doing.</strong></p>
        </b-alert>
        <div class="row mb-3">
          <b-col cols="12">
            <h3 v-translate>Technical Contact Information</h3>
            <p v-translate="{siteName: this.siteName}">Name and e-mail address of the Technical Contact person in your Company. We will send all emails and communications regarding your Company operation webhooks/API applications/technical news/etc. in %{siteName} to this contact person. Please, keep it updated.</p>
            <p><small v-translate>Note: If you don't set a Technical Contact all technical communications will be sent to the Commercial Contact.</small></p>
            <form @submit.prevent="updateTechnicalContact" class="row">
              <div class="form-group col-12 col-md-6">
                <input type="text" :placeholder="$gettext('Name')" class="form-control" v-model="technical_contact_name" required>
              </div>
              <div class="form-group col-12 col-md-6">
                <input type="email" :placeholder="$gettext('E-mail')" class="form-control" v-model="technical_contact_email" required>
              </div>
              <b-col cols="12">
                <b-button variant="primary" size="sm" type="submit" v-translate>Update Technical Contact</b-button>
              </b-col>
            </form>
          </b-col>
        </div>
        <div class="row mb-3" v-if="applications.length > 0">
          <h3 class="col-12" v-translate>API credentials</h3>
          <div class="col-12" v-if="applications.findIndex(x => x.platform_id === null || x.platform.name === 'Moova') !== -1">
            <div v-if="isCompanyAdmin">
              <p><translate>IMPORTANT: When you click "Generate new secret key" button, current key WILL STOP WORKING IMMEDIATELY.</translate></p>
            </div>
            <div v-else>
              <b-alert variant="warning" show>
                <translate>The default API Application must be managed by your Company Admin user.</translate>
              </b-alert>
            </div>
            <b-card v-for="(app, index) in applications" v-if="app.platform_id === null || app.platform.name === 'Moova'" :key="index">
              <h4>{{app.name}}</h4>
              <dl>
                <dt v-translate>Application ID</dt>
                <dd>{{app.id}}</dd>
                <dt v-translate>Secret Key</dt>
                <dd>{{app.key}}<span v-if="isCompanyAdmin">&nbsp;&nbsp;&nbsp;<b-button variant="primary" size="sm" @click="generateNewKey(app.id)" v-b-modal.modalNewSecret v-translate>Generate new secret key</b-button></span></dd>
              </dl>
              <h5 v-translate>Notifications' Webhook</h5>
              <b-form-checkbox v-model="app.webhook_enabled" @input="setEditingWebhook(app); submit()" :disabled="!isCompanyAdmin" :value="1" :unchecked-value="0" switch>
                <translate>Webhook Enabled</translate>
              </b-form-checkbox>
              <dl>
                <dt v-translate>Webhook URL</dt>
                <dd v-if="app.webhook_url">{{app.webhook_url}}<span v-if="isCompanyAdmin">&nbsp;&nbsp;&nbsp;<b-button variant="primary" size="sm" v-b-modal.modalEditWebhook @click="setEditingWebhook(app)" v-translate>Edit Webhook Data</b-button></span></dd>
                <dd v-else><translate>No URL defined yet</translate> <span v-if="isCompanyAdmin">&nbsp;&nbsp;&nbsp;<b-button variant="primary" size="sm" v-b-modal.modalEditWebhook @click="setEditingWebhook(app)" v-translate>Add Webhook Data</b-button></span></dd>
              </dl>
              <dl v-if="app.webhook_url">
                <dt v-translate>Webhooks to Reprocess</dt>
                <dd>
                  <span
                    :translate-n="app.stalledWebhooksCount"
                    v-translate="{stalledWebhooksCount: app.stalledWebhooksCount}"
                    translate-plural="There are %{stalledWebhooksCount} webhooks to reprocess"
                  >
                    There is %{stalledWebhooksCount} webhook to reprocess
                  </span>
                  <b-button v-if="app.stalledWebhooksCount > 0" variant="primary" size="sm" @click="resendStalledWebhooks(app)" :disabled="resending"><b-spinner small style="vertical-align: middle;" v-if="resending" /> <translate>Send To The Reprocess Queue</translate></b-button>
                </dd>
              </dl>
            </b-card>
          </div>
          <div class="col-12" v-else>
            <p><translate>Your company doesn't have any Application yet.</translate></p>
            <p v-if="isCompanyAdmin">
              <b-button size="sm" variant="primary" @click="generateApplication" v-translate>
                Generate default API Application &amp; Secret Key
              </b-button>
            </p>
            <b-alert variant="warning" show v-else>
              <translate>The default API Application must be generated and managed by your Company Admin user.</translate>
            </b-alert>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="modalNewSecret" ref="modalNewSecret" :hide-footer="true">
      <div slot="modal-title" v-translate>New API Credentials</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <div class="w-100">
        <p v-translate>Secret key WILL NOT be visible again once you close this window. Please take note, otherwise you will need to regenerate a new one.</p>
        <div class="form-group col-md-12">
          <label class="control-label" v-translate>Application ID</label> <a href="#" v-if="newKeyData.id" @click.prevent="copyToClipboard('newKeyData-id')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
          <input class="form-control col-md-12" type="text" v-model="newKeyData.id" id="newKeyData-id" readonly>
        </div>
      </div>
      <div class="form-group col-md-12">
        <label class="control-label" v-translate>Secret Key</label> <a href="#" v-if="newKeyData.key" @click.prevent="copyToClipboard('newKeyData-key')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
        <input class="form-control col-md-12" type="text" v-model="newKeyData.key" id="newKeyData-key" readonly>
      </div>
    </b-modal>

    <b-modal id="modalEditWebhook" ref="modalEditWebhook" size="lg" hide-footer @hidden="editingWebhook = {}">
      <div slot="modal-title" v-translate>Edit Webhook</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <form class="" role="form" @submit.prevent="submit">
        <div class="row">
          <h3 class="col-12" v-translate>Notifications' Webhook Definition</h3>
          <b-col cols="12" v-if="!isCompanyAdmin">
            <b-alert variant="warning" show>
              <translate>The Notifications' Webhook data must be managed by your Company Admin user.</translate>
            </b-alert>
          </b-col>
          <h4 class="col-12 mt-3" v-translate>Basic data</h4>
          <div class="form-group col-md-12">
            <b-form-checkbox v-model="editingWebhook.webhook_enabled" :disabled="!isCompanyAdmin" :value="1" :unchecked-value="0" switch>
              <translate>Webhooks Enabled</translate>
            </b-form-checkbox>
          </div>
          <div class="form-group col-md-12">
            <label class="control-label" v-translate>Webhook URL</label>
            <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_url" :disabled="!isCompanyAdmin">
          </div>
          <div class="form-group col-md-12">
            <label class="control-label" v-translate>Webhook Method</label><br>
            <b-form-select v-model="editingWebhook.webhook_method" class="form-control col-md-4" required :disabled="!isCompanyAdmin">
              <option :value="null" v-translate>Please select an option</option>
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </b-form-select>
          </div>
          <h4 class="col-12 mt-4" v-translate>Headers</h4>
          <div class="col-md-12">
            <div class="row" v-for="(headerField, index) in editingWebhook.webhook_headers" :key="index">
              <div class="form-group col-md-4">
                <label class="control-label" v-translate>Header Key</label>
                <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_headers[index].headerKey" :required="editingWebhook.webhook_headers[index].headerValue.length > 0" :disabled="!isCompanyAdmin">
              </div>
              <div class="form-group col-md-6">
                <label class="control-label" v-translate>Header Value</label>
                <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_headers[index].headerValue" :required="editingWebhook.webhook_headers[index].headerKey.length > 0" :disabled="!isCompanyAdmin">
              </div>
              <div class="form-group col-md-2" v-if="index !== 0">
                <label class="control-label text-white">&nbsp;</label><br>
                <b-button variant="primary" size="sm" @click="editingWebhook.webhook_headers.splice(index, 1)" v-translate :disabled="!isCompanyAdmin">Remove</b-button>
              </div>
            </div>
            <div class="text-right">
              <b-button variant="primary" size="sm" @click="editingWebhook.webhook_headers.push(Object.assign({}, headerModel))" v-translate :disabled="!isCompanyAdmin">Add header</b-button>
            </div>
          </div>
        </div>
        <div class="row text-left">
          <div class="form-group col-12 form-actions">
            <label class="col-md-3 control-label"></label>
            <div class="col-md-8">
              <b-button variant="primary" size="lg" type="submit" v-translate :disabled="!isCompanyAdmin">Save Changes</b-button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </b-container>
</template>
<script>
import CompanyNavigation from '@/components/CompanyNavigation'
import { mapGetters } from 'vuex'
import { FETCH_PROFILE } from '@/store/actions.type'
export default {
  name: 'ProfileInvoicingDataPage',
  title() {
    return this.$gettext('API/Webhook')
  },
  components: {
    CompanyNavigation
  },
  data() {
    return {
      breadcrumb: [{
        text: 'Home',
        to: { name: 'index' }
      }, {
        text: 'Profile',
        to: { name: 'profile' }
      }, {
        text: 'Invoicing Data',
        active: true
      }],
      formSaved: false,
      dismissSecs: 10,
      dismissCountDown: 0,
      company: {
        webhook_enabled: 1,
        webhook_url: null,
        webhook_method: null,
        webhook_headers: []
      },
      headerModel: {
        headerKey: '',
        headerValue: ''
      },
      applications: [],
      currentApp: null,
      newKeyData: {},
      editingWebhook: {
        webhook_enabled: 1,
        webhook_url: null,
        webhook_method: null,
        webhook_headers: []
      },
      resending: false,
      technical_contact_name: '',
      technical_contact_email: ''
    }
  },
  computed: {
    isCompanyAdmin() {
      return this.$store.getters.profile.company && this.$store.getters.profile.roles.findIndex(x => x.name === 'company-admin') !== -1
    },
    ...mapGetters([
      'user',
      'isLoading'
    ])
  },
  created() {
    this.fetchProfile()
  },
  methods: {
    async fetchApplications() {
      await this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '?includes[]=applications')
        .then(async response => {
          this.fetchStalledWebhooksCount(response.data.applications)
            .then(results => {
              this.applications = results
            })
        })
    },
    generateApplication() {
      let payload = {
        name: this.user.company.name,
        company_id: this.user.company_id,
        platform_id: null,
        active: true
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'applications', payload)
        .then(response => {
          this.$bvModal.show('modalNewSecret')
          this.newKeyData = response.data
          this.fetchProfile()
        })
    },
    generateNewKey(appId) {
      if (confirm(this.$gettext('Are you sure COMPLETELY SURE you want to generate a NEW SECRET KEY?\n\nIMPORTANT: Remember that your API integration WILL STOP WORKING IMMEDIATELY with the current secret key and you will need to make the necessary changes in your own app/system/integration to get it back to work.\n\nThis operation CANNOT BE UNDONE.')) === true) {
        this.$bvModal.show('modalNewSecret')
        this.$http.post(this.$appConfig.api.baseUrl + 'applications/' + appId + '/generate-api-key')
          .then(response => {
            this.newKeyData = response.data
          })
        this.fetchProfile()
      }
    },
    async fetchProfile() {
      await this.$store.dispatch(FETCH_PROFILE)
        .then(async response => {
          if (response.company) {
            if (response.company.applications.length > 0) {
              this.fetchStalledWebhooksCount(response.company.applications)
                .then(results => {
                  this.applications = results
                })
            }
            if (this.user.billing_data !== null) {
              this.invoicingData = JSON.parse(this.user.billing_data)
            } else {
              for (let i in this.formFields) {
                this.invoicingData[i] = null
              }
            }
            if (this.user.profile_address_id === null) {
              this.user.profile_address = {
                place: {
                  formatted_address: null
                }
              }
            }
            this.company.webhook_enabled = this.user.company.webhook_enabled
            this.company.webhook_url = this.user.company.webhook_url
            this.company.webhook_method = this.user.company.webhook_method
            let that = this
            let currentHeaders = this.user.company.webhook_headers || []
            if (currentHeaders.length > 0) {
              currentHeaders.forEach(function (val) {
                that.company.webhook_headers.push({
                  headerKey: Object.keys(val)[0],
                  headerValue: Object.values(val)[0]
                })
              })
            } else {
              if (this.company.webhook_headers.length === 0) {
                this.company.webhook_headers.push({
                  headerKey: '',
                  headerValue: ''
                })
              }
            }
            this.technical_contact_name = this.user.company.technical_contact_name
            this.technical_contact_email = this.user.company.technical_contact_email
          }
        }, error => {
          let obj = {
            title: 'ERROR',
            message: error.body.message,
            type: 'error',
            customCloseBtnText: this.$gettext('Close')
          }
          this.$parent.$refs.simplert.openSimplert(obj)
        })
    },
    async fetchStalledWebhooksCount(applications) {
      for (let i = 0; i < applications.length; i++) {
        if (applications[i].webhook_url) {
          let params = {
            limit: 1,
            page: 0,
            sort: [
              {
                key: 'created_at',
                direction: 'asc'
              }
            ],
            filter_groups: [
              {
                filters: [
                  ['application_id', 'eq', applications[i].id],
                  ['status', 'in', ['givenup', 'cancelled']]
                ]
              }
            ]
          }
          await this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '/webhooks', {params})
            .then(async webhooks => {
              applications[i].stalledWebhooksCount = webhooks.body.pagination.total
              applications[i].dateFrom = webhooks.body.data[0]?.created_at || null

              params.sort[0].direction = 'desc'
              await this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '/webhooks', {params})
                .then(webhooks => {
                  applications[i].dateFinal = webhooks.body.data[0]?.created_at || null
                })
            })
        } else {
          applications[i].fetchStalledWebhooksCount = 0
        }
      }
      return applications
    },
    async resendStalledWebhooks(app) {
      if (app.dateFrom && app.dateFinal) {
        this.resending = true
        return await this.doResendStalledWebhooks(app, app.dateFrom)
      }
    },
    doResendStalledWebhooks(app, dateFrom) {
      const dateTo = this.moment.utc(dateFrom).add(5, 'days').format('YYYY-MM-DD HH:mm:ss')
      let params = {
        filter_groups: [
          {
            filters: [
              ['application_id', 'eq', app.id],
              ['status', 'in', ['givenup', 'cancelled']],
              ['created_at', 'bt', [dateFrom, dateTo]]
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '/webhooks/bulk-resend', {params})
        .then(response => {
          if (this.moment.utc(dateTo) < this.moment.utc(app.dateFinal)) {
            this.doResendStalledWebhooks(app, dateTo)
          } else {
            this.fetchProfile()
            this.resending = false
          }
        })
    },
    updateTechnicalContact() {
      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id, {technical_contact_name: this.technical_contact_name, technical_contact_email: this.technical_contact_email})
        .then(response => {
          this.fetchProfile()
          this.showAlert()
        })
    },
    setEditingWebhook(app) {
      this.editingWebhook = {
        id: null,
        webhook_enabled: 1,
        webhook_url: null,
        webhook_method: null,
        webhook_headers: []
      }
      this.editingWebhook.id = app.id
      this.editingWebhook.webhook_enabled = app.webhook_enabled
      this.editingWebhook.webhook_url = app.webhook_url
      this.editingWebhook.webhook_method = app.webhook_method
      let that = this
      let currentHeaders = app.webhook_headers || []
      if (currentHeaders.length > 0) {
        currentHeaders.forEach(function (val) {
          that.editingWebhook.webhook_headers.push({
            headerKey: Object.keys(val)[0],
            headerValue: Object.values(val)[0]
          })
        })
      } else {
        if (!app.webhook_headers || app.webhook_headers.length === 0) {
          this.editingWebhook.webhook_headers = [{
            headerKey: '',
            headerValue: ''
          }]
        }
      }
    },
    submit() {
      let headers = []
      this.editingWebhook.webhook_headers.forEach(function (val) {
        if (val.headerKey) {
          let obj = {}
          obj[ val.headerKey ] = val.headerValue
          headers.push(obj)
        }
      })

      let params = {
        webhook_enabled: this.editingWebhook.webhook_enabled,
        webhook_url: this.editingWebhook.webhook_url,
        webhook_method: this.editingWebhook.webhook_method,
        webhook_headers: this.editingWebhook.webhook_headers[0].headerKey.length > 0 ? headers : null
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '/applications/' + this.editingWebhook.id, params)
        .then(response => {
          this.editingWebhook = {
            id: null,
            webhook_enabled: 1,
            webhook_url: null,
            webhook_method: null,
            webhook_headers: []
          }
          this.fetchProfile()
          this.$refs.modalEditWebhook.hide()
        })
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    },
    copyToClipboard(el) {
      let testingCodeToCopy = document.querySelector('#' + el)

      testingCodeToCopy.setAttribute('type', 'text')
      testingCodeToCopy.select()

      try {
        let successful = document.execCommand('copy')
        let msg = successful ? this.$gettext('Code was copied successfully') : this.$gettext('Code was copied unsuccessfully')
        alert(msg)
      } catch (err) {
        alert(this.$gettext('Oops, unable to copy'))
      }
    }
  }
}

</script>
